import { computed, inject, Injector, Type } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';

import {
  CancelSubmission,
  SubmissionDetailSignalState,
  SubmissionResponse,
  UpdateSubmissionStagePayload,
} from '../../types';
import { SubmissionsService } from '../../services/submissions.service';
import {
  SUBMISSION_DETAIL_INITIAL_STATE,
  SUBMISSION_DETAIL_INITIAL_STATE_TOKEN,
} from '../../constants/submission-detail.constant';
import { SiteService } from '@submissions/v1/services/site.service';
import { HttpParams } from '@angular/common/http';

import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, map, of, pipe, switchMap, take, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { LoadingService } from '@loader/spinner';
import {
  OpenDialogState,
  withDialogFeatures,
  withSnackBarFeature,
} from '@data-access/shared';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { WcgTableUtilsService } from '@shared/services/wcg-table-utils.service';
import {
  SubmissionSite,
  SubmissionSitesListResponse,
} from '@data-access/sites';
import { SubmissionSetupService } from '../../services/submission-setup.service';
import { normalizeOrganizationAssignmentRoles } from '../../utils/submission.utils';
import { WorkspaceTypes } from '../../../../submission-setup/constants';
export const submissionDetailSignalStore = signalStore(
  withDevtools('submissionDetailSignalStore'),
  withState(() => inject(SUBMISSION_DETAIL_INITIAL_STATE_TOKEN)),
  withProps(
    (
      store,
      _submissionService = inject(SubmissionsService),
      _siteService = inject(SiteService),
      _wcgTableUtilService = inject(WcgTableUtilsService),
      _loadingService = inject(LoadingService),
      _submissionSetupService = inject(SubmissionSetupService),
    ) => ({
      _injector: inject(Injector),
      _loadingService,
      _submissionService,
      _siteService,
      _wcgTableUtilService,
      _submissionSetupService,
      store,
    }),
  ),
  withDialogFeatures(),
  withSnackBarFeature(),
  withComputed(({ ...store }) => ({
    $current_stage: computed(() => store.submission()?.current_stage ?? null),
    $error: computed(() => store.error()),
    $isLoading: computed(() => store.isLoading()),
    $submission: computed(() => {
      const submission = store.submission();
      return submission;
    }),
    $submissionId: computed(() => store.submission()?.id ?? ''),
    $hasOutcomeDocuments: computed(() => store.hasOutcomeDocuments()),
    $sites: computed(() => store.sites()),
    $appealSubmissionSetupPath: computed(() =>
      store.appealSubmissionSetupPath(),
    ),
    $isSubmissionCancelled: computed(() => store.isSubmissionCancelled()),
    $organiationStatus: computed(() => store.organiationStatus()),
    $organizationValidationAssignedTypes: computed(() =>
      store.organizationValidationAssignedTypes(),
    ),
    $organizationValidationUnassignedTypes: computed(() =>
      store.organizationValidationUnassignedTypes(),
    ),
    $reloadContactOrgs: computed(() => store.reloadContactOrgs()),
  })),
  withMethods(
    ({
      _submissionService,
      _wcgTableUtilService,
      _siteService,
      _loadingService,
      _submissionSetupService,
      openDialog,
      ...store
    }) => {
      const _updateSubmission = (response: SubmissionResponse) => {
        patchState(store, {
          isLoading: false,
          submission: { ...response },
        });
      };
      const _loadSubmission = rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ id }) =>
            _submissionService.getSubmission(id).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => _updateSubmission(response),
              }),
            ),
          ),
        ),
      );

      const _loadOutcomeDocuments = rxMethod<{ id: string }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ id }) =>
            _submissionService.loadOutcomeDocuments(id).pipe(
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error, isLoading: false }),
                finalize: () => patchState(store, { isLoading: false }),
                next: (response) => {
                  patchState(store, {
                    hasOutcomeDocuments: response.total > 0,
                  });
                },
              }),
            ),
          ),
        ),
      );
      /**
       * Method to update the submission stage.
       * @param workspaceId - The ID of the workspace to update the submission stage for.
       **/
      const _updateSubmissionStage = rxMethod<UpdateSubmissionStagePayload>(
        pipe(
          tap(() => {
            _loadingService.showLoading();
            patchState(store, {
              error: null,
              isLoading: true,
            });
          }),
          switchMap((payload) =>
            _submissionService.updateSubmissionStage(payload).pipe(
              tapResponse({
                error: (error: Error) => patchState(store, { error }),
                finalize: () => {
                  patchState(store, { isLoading: false });
                  if (payload.organizationValidationData) {
                    patchState(store, { reloadContactOrgs: true });
                  }
                  _loadingService.hideLoading();
                },
                next: (response) => {
                  _loadSubmission({ id: response.id });
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Method to check if an organization is assigned to the submission. This is required before moving to review stage, and needs to be checked before review stage transition as the assigned organization can be changed while the submission is in progress.
       **/
      const _organizationValidation = rxMethod<{
        workspaceId: string;
      }>(
        pipe(
          tap(() => {
            _loadingService.showLoading();
            patchState(store, {
              isLoading: true,
              organiationStatus: 'checking',
              organizationValidationAssignedTypes: [],
              organizationValidationUnassignedTypes: [],
              reloadContactOrgs: false
            });
          }),
          switchMap(({ workspaceId }) =>
            _submissionService.organizationValidation(workspaceId).pipe(
              tapResponse({
                error: () =>
                  patchState(store, {
                    isLoading: false,
                    organizationValidationAssignedTypes: [],
                    organizationValidationUnassignedTypes: [],
                  }),
                finalize: () => {
                  patchState(store, { isLoading: false });
                  _loadingService.hideLoading();
                },
                next: (response) => {
                  patchState(store, {
                    isLoading: false,
                    organiationStatus: response.status,
                    organizationValidationAssignedTypes:
                      normalizeOrganizationAssignmentRoles(
                        response.assigned_organization_types,
                      ),
                    organizationValidationUnassignedTypes:
                      normalizeOrganizationAssignmentRoles(
                        response.unassigned_organization_types,
                      ),
                  });
                },
              }),
            ),
          ),
        ),
      );
      const _updateSubmissionName = rxMethod<{
        workspaceId: string;
        value: string;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ workspaceId, value }) =>
            _submissionService.updateSubmissionName(workspaceId, value).pipe(
              tapResponse({
                complete: () => patchState(store, { isLoading: false }),
                error: (error: Error) => patchState(store, { error }),
                next: (response: SubmissionResponse) => {
                  const submission = store.submission();
                  if (submission) {
                    patchState(store, {
                      submission: { ...submission, name: response.name },
                    });
                  }
                },
              }),
            ),
          ),
        ),
      );

      /**
       * Method to complete an IBC submission.
       * @param workspaceId - The ID of the workspace to complete the IBC submission for.
       **/
      const _completeIbcSubmission = rxMethod<{ workspaceId: string }>(
        pipe(
          tap(() => {
            _loadingService.showLoading();
            patchState(store, {
              error: null,
              isLoading: true,
            });
          }),
          switchMap(({ workspaceId }) =>
            _submissionService.completeIbcSubmission(workspaceId).pipe(
              tapResponse({
                error: (error: Error) => patchState(store, { error }),
                finalize: () => {
                  patchState(store, { isLoading: false });
                  _loadingService.hideLoading();
                },
                next: (response) => {
                  _loadSubmission({ id: response.id });
                },
              }),
            ),
          ),
        ),
      );

      const _loadSubmissionSites = rxMethod<{
        submissionId: string;
        queryParams?: HttpParams;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(({ submissionId, queryParams }) => {
            const _queryParams =
              queryParams ||
              _wcgTableUtilService.generateTableQueryParams({
                page: 1,
                pageSize: 10,
              });

            return _siteService
              .getSubmissionSites(submissionId, _queryParams)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, { error, isLoading: false }),
                  finalize: () => patchState(store, { isLoading: false }),
                  next: (response: SubmissionSitesListResponse) => {
                    patchState(store, {
                      sites: response,
                    });
                  },
                }),
              );
          }),
        ),
      );

      /**
       * Method to delete a submission.
       * @param workspaceId - The ID of the workspace to delete.
       * @param reason_for_delete - The reason for deleting the submission.
       * @param isInternal - Whether the user is internal.
       * @returns An Observable of the HTTP response.
       */
      const _deleteSubmission = (
        workspaceId: string,
        reason_for_delete: string,
        isInternal: boolean,
      ) => {
        patchState(store, {
          error: null,
          isLoading: true,
        });
        return _submissionService
          .removeSubmission(workspaceId, reason_for_delete, isInternal)
          .pipe(
            tapResponse({
              error: (error: Error) =>
                patchState(store, { error, isLoading: false }),
              finalize: () => patchState(store, { isLoading: false }),
              next: () => {
                // Reset state after successful deletion
                patchState(store, { ...SUBMISSION_DETAIL_INITIAL_STATE });
              },
            }),
          );
      };

      const _resetState = (submissionId?: string) => {
        if (
          submissionId &&
          submissionId === store.submission()?.int_id?.toString()
        ) {
          patchState(store, {
            ...SUBMISSION_DETAIL_INITIAL_STATE,
            sites: store.sites(),
            hasOutcomeDocuments: store.hasOutcomeDocuments(),
          });
        } else {
          patchState(store, { ...SUBMISSION_DETAIL_INITIAL_STATE });
        }
      };

      /**
       * rxMethod: fetches workspace types, finds Modification to Research type ID,
       * and sets appealSubmissionSetupPath. Caller should navigate then clear via clearAppealSubmissionSetupPath.
       */
      const _openAppealSubmissionSetup = rxMethod<{ submissionId: string }>(
        pipe(
          switchMap(({ submissionId }) =>
            _submissionSetupService.getSubmissionTypes().pipe(
              map((response) => {
                const mtrType = response.data.find(
                  (type) => type.name === WorkspaceTypes.mtr,
                );
                if (!mtrType) {
                  console.error(
                    'Modification to Research workspace type not found',
                  );
                  return null;
                }
                return `/submission-setup/${mtrType.id}/appeal-submission-setup/${submissionId}`;
              }),
              catchError((error) => {
                console.error('Error fetching workspace types:', error);
                return of(null);
              }),
              tap((path) =>
                patchState(store, { appealSubmissionSetupPath: path }),
              ),
            ),
          ),
        ),
      );

      const clearAppealSubmissionSetupPath = () =>
        patchState(store, { appealSubmissionSetupPath: null });

      /*
       * @HANDLE CANCEL SUBMISSION REQUEST METHOD STARTS
       * */
      const _cancelSubmissionRequest = rxMethod<{
        workspaceId: string;
        reasonForCancellation: string;
      }>(
        pipe(
          tap(() => patchState(store, { error: null, isLoading: true })),
          switchMap(({ workspaceId, reasonForCancellation }) =>
            _submissionService
              .cancelSubmission(workspaceId, reasonForCancellation)
              .pipe(
                tapResponse({
                  error: (error: Error) =>
                    patchState(store, {
                      error,
                      isLoading: false,
                      isSubmissionCancelled: false,
                    }),
                  next: (response: CancelSubmission) => {
                    if (response && response.message.length > 0) {
                      store.displaySnackBar(
                        'Submission was successfully cancelled',
                        'Close',
                        5000,
                      );
                      patchState(store, {
                        isSubmissionCancelled: true,
                        error: null,
                      });
                    }
                  },
                  finalize: () => patchState(store, { isLoading: false }),
                }),
              ),
          ),
        ),
      );
      const _handleCancelSubmissionRequest = (
        component: OpenDialogState,
        submissionId: string,
      ) => {
        const _dialogRef = openDialog(component);
        if (_dialogRef) {
          _dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe({
              next: (data: string) => {
                if (data?.length > 0) {
                  _cancelSubmissionRequest({
                    workspaceId: submissionId,
                    reasonForCancellation: data,
                  });
                }
              },
            });
        }
      };
      /**
       * @HANDLE CANCEL SUBMISSION REQUEST METHOD ENDS
       * */

      return {
        loadOutcomeDocuments: (submissionId: string) =>
          _loadOutcomeDocuments({ id: submissionId }),
        loadSubmission: (submissionId: string) =>
          _loadSubmission({ id: submissionId }),
        loadSubmissionSites: (submissionId: string, queryParams?: HttpParams) =>
          _loadSubmissionSites({ submissionId, queryParams }),
        openDialog,
        resetState: (submissionId?: string) => _resetState(submissionId),
        updateSubmission: (submission: SubmissionResponse) =>
          patchState(store, { submission: { ...submission } }),
        updateSubmissionName: (workspaceId: string, value: string) =>
          _updateSubmissionName({ value, workspaceId }),
        updateSubmissionStage: (payload: UpdateSubmissionStagePayload) =>
          _updateSubmissionStage(payload),

        organizationValidation: (workspaceId: string) =>
          _organizationValidation({ workspaceId }),
        completeIbcSubmission: (workspaceId: string) =>
          _completeIbcSubmission({ workspaceId }),
        deleteSubmission: (
          workspaceId: string,
          reason_for_delete: string,
          isInternal: boolean,
        ) => _deleteSubmission(workspaceId, reason_for_delete, isInternal),
        updateSubmissionSites: (data: SubmissionSite[], total: number) =>
          patchState(store, {
            sites: {
              ...store.sites(),
              data,
              total,
            },
          }),
        openAppealSubmissionSetup: (submissionId: string) =>
          _openAppealSubmissionSetup({ submissionId }),
        clearAppealSubmissionSetupPath,
        handleCancelSubmissionRequest: (
          component: OpenDialogState,
          submissionId: string,
        ) => _handleCancelSubmissionRequest(component, submissionId),
        errorHandler: (errorMessage: string) =>
          store.displaySnackBar(errorMessage, 'Close', 5000),
      };
    },
  ),
) satisfies Type<SubmissionDetailSignalState>;
